import { useMutation, gql } from '@apollo/client';
import { setStudioExactLocation, updateStudio } from '../graphql/mutations/userStudioMutations';
import {   
  CREATE_AVAILABILITY, 
  CREATE_STUDIO_EQUIPMENT,
  UPDATE_STUDIO_EQUIPMENT,
  DELETE_STUDIO_EQUIPMENT
} from "../graphql/queries/createStudioQueries";
import { useApolloClient } from '@apollo/client';
import { getStudio } from '../graphql/queries/queriesStudio';
import { SKIP_GLOBAL_ERROR } from '../util/notifyActionError';

export const useUpdateStudio = () => {
  const client = useApolloClient();
  
  const [updateStudioMutation, { loading: studioLoading, error: studioError }] = useMutation(updateStudio, {
    update(cache, { data }) {
      const updatedStudio = data?.updateStudio;
      if (!updatedStudio?.id) return;

      cache.modify({
        id: cache.identify(updatedStudio),
        fields: {
          tags(existingTags = { items: [], nextToken: null }) {
            const updatedTagItems = updatedStudio.tags?.items;
            if (!Array.isArray(updatedTagItems)) {
              return existingTags;
            }

            return {
              __typename: existingTags?.__typename || 'ModelStudioTagConnection',
              items: updatedTagItems,
              nextToken: updatedStudio.tags?.nextToken ?? existingTags?.nextToken ?? null,
            };
          },
          status(existingStatus) {
            return updatedStudio.status ?? existingStatus;
          },
        },
      });
    },
  });
  const [createAvailability] = useMutation(CREATE_AVAILABILITY);
  const [createEquipment] = useMutation(CREATE_STUDIO_EQUIPMENT);
  const [updateEquipment] = useMutation(UPDATE_STUDIO_EQUIPMENT);
  const [deleteEquipment] = useMutation(DELETE_STUDIO_EQUIPMENT);

  const OPENCAGE_API_KEY = process.env.REACT_APP_OPENCAGE_API_KEY;
  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  };

  const getCoordinates = async (address) => {
    try {
      const response = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(address)}&key=${OPENCAGE_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry;
        return { latitude: lat, longitude: lng };
      }
      throw new Error('No coordinates found for this address');
    } catch (error) {
      throw error;
    }
  };

  const handleStudioUpdate = async (studioId, studioData, studio = null) => {
    try {
      const resolvedStudioId = studioId || studio?.id;
      if (!resolvedStudioId) {
        throw new Error('Missing studio id for update');
      }

      let coordinates = {};
      
      const cleanStudioData = { ...studioData };
      delete cleanStudioData.tags;

      // Only get new coordinates if address fields were updated
      if (cleanStudioData.streetAndNumber && cleanStudioData.city && cleanStudioData.postcode && cleanStudioData.country) {
        const fullAddress = `${cleanStudioData.streetAndNumber}, ${cleanStudioData.city}, ${cleanStudioData.postcode}, ${cleanStudioData.country}`;
        try {
          coordinates = await getCoordinates(fullAddress);
          
          // Verify coordinates have actual values
          if (!coordinates?.latitude || !coordinates?.longitude) {
            // Fall back to existing coordinates if available
            if (cleanStudioData.latitude != null && cleanStudioData.longitude != null) {
              coordinates = { latitude: cleanStudioData.latitude, longitude: cleanStudioData.longitude };
            }
          }
        } catch (geoError) {
          // Fall back to existing coordinates if available
          if (cleanStudioData.latitude != null && cleanStudioData.longitude != null) {
            coordinates = { latitude: cleanStudioData.latitude, longitude: cleanStudioData.longitude };
          }
        }
      } else {
        // Use existing coordinates if available
        if (cleanStudioData.latitude != null && cleanStudioData.longitude != null) {
          coordinates = { latitude: cleanStudioData.latitude, longitude: cleanStudioData.longitude };
        }
      }

      const updateInput = buildStudioUpdateInput({
        resolvedStudioId,
        cleanStudioData,
        coordinates
      });

      const { data: studioResponse } = await updateStudioMutation({
        variables: {
          input: updateInput
        },
        ...SKIP_GLOBAL_ERROR,
      });

      const exactLocationInput = buildStudioExactLocationInput({
        resolvedStudioId,
        cleanStudioData,
        coordinates,
      });

      if (exactLocationInput) {
        await client.mutate({
          mutation: setStudioExactLocation,
          variables: {
            input: exactLocationInput,
          },
          ...SKIP_GLOBAL_ERROR,
        });
      }

      // Fetch the complete studio data using getStudio query (DynamoDB update responses only return changed fields)
      const GET_STUDIO_QUERY = gql`${getStudio}`;
      const { data: refreshedStudioData } = await client.query({
        query: GET_STUDIO_QUERY,
        variables: { id: resolvedStudioId },
        fetchPolicy: 'network-only'
      });

      const refreshedStudio = refreshedStudioData?.getStudio;

      // Update Apollo cache with the refreshed studio data so components automatically update
      if (refreshedStudio) {
        try {
          client.writeQuery({
            // Use the exact same query shape we just fetched to avoid
            // Apollo invariant warnings from mismatched cache selections.
            query: GET_STUDIO_QUERY,
            variables: { id: resolvedStudioId },
            data: refreshedStudioData
          });
          
          // Trigger a refetch to ensure cache is fresh
          await client.refetchQueries({
            include: ['GetStudio', 'StudiosForServiceSelection']
          });
        } catch (cacheError) {
          console.warn('Error updating cache:', cacheError);
        }
      }

      // Handle availability updates if provided
      if (cleanStudioData.availability) {
        const dayMapping = {
          Mon: 1,
          Di: 2,
          Mi: 3,
          Do: 4,
          Fr: 5,
          Sa: 6,
          So: 7
        };
  
        const formattedAvailabilities = Object.entries(cleanStudioData.availability)
          .filter(([_, dayData]) => dayData.isActive)
          .flatMap(([day, dayData]) => {
            if (dayData.is24Hours) {
              return [{
                studioID: resolvedStudioId,
                dayOfWeek: dayMapping[day],
                startTime: "00:00",
                endTime: "23:59"
              }];
            }
            
            return dayData.timeSlots
              .filter(slot => slot.start && slot.end)
              .map(slot => ({
                studioID: resolvedStudioId,
                dayOfWeek: dayMapping[day],
                startTime: slot.start,
                endTime: slot.end
              }));
          });

        await Promise.all(
          formattedAvailabilities.map(availability =>
            createAvailability({
              variables: {
                input: availability
              },
              ...SKIP_GLOBAL_ERROR,
            })
          )
        );
      }

      // Handle equipment updates if provided
      if (cleanStudioData.equipment) {
        const incomingEquipmentMap = cleanStudioData.equipment || {};
        const existingEquipmentList = Array.isArray(studio?.equipment?.items) ? studio.equipment.items : [];
        const existingByCategory = new Map(
          existingEquipmentList
            .filter(entry => entry?.category && entry?.id)
            .map(entry => [entry.category, entry])
        );

        const normalizedIncomingEntries = Object.entries(incomingEquipmentMap).map(([category, items]) => ([
          category,
          (Array.isArray(items) ? items : [])
            .filter(item => item && typeof item === 'object' && item.name)
            .map(item => ({
              id: String(item.id || generateId()),
              name: String(item.name).trim(),
              count: Math.max(1, Number(item.count || 1))
            }))
        ]));

        const runEquipmentMutation = async (opName, input, runner) => {
          try {
            const result = await runner();
            return result;
          } catch (mutationError) {
            throw mutationError;
          }
        };

        // Update or delete existing equipment categories
        await Promise.all(
          existingEquipmentList.map(existingEntry => {
            const category = existingEntry?.category;
            if (!category || !existingEntry?.id) return Promise.resolve();

            const incomingItems = incomingEquipmentMap[category];
            const normalizedItems = (Array.isArray(incomingItems) ? incomingItems : [])
              .filter(item => item && typeof item === 'object' && item.name)
              .map(item => ({
                id: String(item.id || generateId()),
                name: String(item.name).trim(),
                count: Math.max(1, Number(item.count || 1))
              }));

            if (normalizedItems.length === 0) {
              return runEquipmentMutation(
                'deleteStudioEquipment',
                { id: existingEntry.id },
                () => deleteEquipment({
                  variables: {
                    input: { id: existingEntry.id }
                  },
                  ...SKIP_GLOBAL_ERROR,
                })
              ).catch((deleteError) => {
                const isUnauthorizedDelete =
                  deleteError?.graphQLErrors?.some(
                    (gqlErr) =>
                      gqlErr?.errorType === 'Unauthorized' ||
                      String(gqlErr?.message || '').includes('Not Authorized to access deleteStudioEquipment')
                  ) ||
                  String(deleteError?.message || '').includes('Not Authorized to access deleteStudioEquipment');

                if (isUnauthorizedDelete) {
                  console.warn('[EquipmentSync] delete unauthorized, skipping', {
                    category,
                    equipmentId: existingEntry.id
                  });
                  return null;
                }

                throw deleteError;
              });
            }

            return runEquipmentMutation(
              'updateStudioEquipment',
              {
                id: String(existingEntry.id),
                category,
                items: normalizedItems
              },
              () => updateEquipment({
                variables: {
                  input: {
                    id: existingEntry.id,
                    category,
                    items: normalizedItems
                  }
                },
                ...SKIP_GLOBAL_ERROR,
              })
            );
          })
        );

        // Create newly added categories
        const createOps = normalizedIncomingEntries
          .filter(([category, items]) => items.length > 0 && !existingByCategory.has(category))
          .map(([category, items]) => {
            const newEquipmentId = generateId();
            return runEquipmentMutation(
              'createStudioEquipment',
              {
                id: newEquipmentId,
                studioID: resolvedStudioId,
                category,
                items
              },
              () => createEquipment({
                variables: {
                  input: {
                    id: newEquipmentId,
                    studioID: resolvedStudioId,
                    category,
                    items
                  }
                },
                ...SKIP_GLOBAL_ERROR,
              })
            );
          });

        if (createOps.length > 0) {
          await Promise.all(createOps);
        }
      }

      // Return the refreshed studio data (complete) instead of the partial mutation response
      return refreshedStudio || studioResponse?.updateStudio;
    } catch (error) {
      throw error;
    }
  };

  return {
    handleStudioUpdate,
    loading: studioLoading,
    error: studioError
  };
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const normalizeOptionalString = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const stringValue = String(value).trim();
  return stringValue || null;
};

const normalizeRequiredString = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return String(value).trim();
};

const normalizeOptionalNumber = (value) => {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

export const buildStudioExactLocationInput = ({ resolvedStudioId, cleanStudioData = {}, coordinates = {} }) => {
  const input = { studioID: resolvedStudioId };

  if (hasOwn(cleanStudioData, 'streetAndNumber')) {
    input.streetAndNumber = normalizeOptionalString(cleanStudioData.streetAndNumber);
  }

  if (hasOwn(cleanStudioData, 'adressLine2')) {
    input.adressLine2 = normalizeOptionalString(cleanStudioData.adressLine2);
  }

  if (hasOwn(cleanStudioData, 'city')) {
    input.city = normalizeRequiredString(cleanStudioData.city);
  }

  if (hasOwn(cleanStudioData, 'postcode')) {
    input.postcode = normalizeRequiredString(cleanStudioData.postcode);
  }

  if (hasOwn(cleanStudioData, 'country')) {
    input.country = normalizeRequiredString(cleanStudioData.country);
  }

  const latitudeSource = coordinates?.latitude !== undefined ? coordinates.latitude : cleanStudioData.latitude;
  const longitudeSource = coordinates?.longitude !== undefined ? coordinates.longitude : cleanStudioData.longitude;
  const shouldIncludeLatitude = coordinates?.latitude !== undefined || hasOwn(cleanStudioData, 'latitude');
  const shouldIncludeLongitude = coordinates?.longitude !== undefined || hasOwn(cleanStudioData, 'longitude');

  if (shouldIncludeLatitude) {
    const latitude = normalizeOptionalNumber(latitudeSource);
    if (latitude !== undefined) {
      input.latitude = latitude;
    }
  }

  if (shouldIncludeLongitude) {
    const longitude = normalizeOptionalNumber(longitudeSource);
    if (longitude !== undefined) {
      input.longitude = longitude;
    }
  }

  return Object.keys(input).length > 1 ? input : null;
};

export const buildStudioUpdateInput = ({ resolvedStudioId, cleanStudioData = {} }) => {
  const updateInput = {
    id: resolvedStudioId,
    ...(cleanStudioData.city ? { city: cleanStudioData.city } : {}),
    ...(cleanStudioData.postcode !== undefined && cleanStudioData.postcode !== null ? { postcode: String(cleanStudioData.postcode) } : {}),
    ...(cleanStudioData.country ? { country: cleanStudioData.country } : {}),
  };

  const fieldsThatCanBeNull = [
    'pricePerHour',
    'minNumberOfHours',
    'tieredPricing',
    'packages',
    'discountRules',
    'extraFees',
    'weekendSurcharge'
  ];

  Object.keys(cleanStudioData).forEach(key => {
    if (!['streetAndNumber', 'adressLine2', 'city', 'postcode', 'country', 'latitude', 'longitude', 'approxLatitude', 'approxLongitude', 'locationLabel', 'locationPrecision', 'equipment'].includes(key)) {
      const value = cleanStudioData[key];

      if (fieldsThatCanBeNull.includes(key)) {
        if (value !== undefined) {
          updateInput[key] = value;
        }
      } else if (value !== null && value !== undefined && value !== '') {
        updateInput[key] = value;
      }
    }
  });

  Object.keys(updateInput).forEach((key) => {
    if (!fieldsThatCanBeNull.includes(key) && (updateInput[key] === null || updateInput[key] === undefined)) {
      delete updateInput[key];
    }

    if (key === 'pricePerHour' && updateInput[key] !== null && (updateInput[key] === '' || Number.isNaN(Number(updateInput[key])))) {
      delete updateInput[key];
    }
  });

  if (updateInput.capacity) {
    delete updateInput.capacity;
  }

  return updateInput;
};
